/**
 * Wrapper di logging per le azioni critiche del collaboratore (start/stop,
 * accetta/rifiuta, invio relazione). Registra ATTEMPTED prima della chiamata e
 * OK/FAIL dopo con lo stesso attemptId: un ATTEMPTED senza esito indica che la
 * promise si è persa. L'errore viene rilanciato per il toast del chiamante.
 */
import { captureClientError, clientErrorLogger } from './clientErrorLogger';

export interface SafeActionContext {
    targetAction: string;             // es. INCARICO_START
    entityType?: string;              // incarico | turno | relazione
    entityId?: number | string;
    payload?: any;                    // sanitizzare (no base64) — si vede in audit
}

/**
 * Esegue `fn` loggando ATTEMPTED prima e OK/FAIL dopo. Ritorna il valore di fn
 * o rilancia l'errore.
 */
export async function safeAction<T>(ctx: SafeActionContext, fn: () => Promise<T>): Promise<T> {
    const attemptId = clientErrorLogger.generateAttemptId();
    // ATTEMPTED va in coda anche se fn() fallisce subito
    captureClientError({
        targetAction: ctx.targetAction,
        outcome: 'ATTEMPTED',
        attemptId,
        entityType: ctx.entityType,
        entityId: ctx.entityId,
        payload: sanitizePayload(ctx.payload),
    });
    try {
        const result = await fn();
        captureClientError({
            targetAction: ctx.targetAction,
            outcome: 'OK',
            attemptId,
            entityType: ctx.entityType,
            entityId: ctx.entityId,
        });
        return result;
    } catch (e: any) {
        captureClientError({
            targetAction: ctx.targetAction,
            outcome: 'FAIL',
            attemptId,
            entityType: ctx.entityType,
            entityId: ctx.entityId,
            httpStatus: e?.response?.status ?? null,
            errorMsg: extractErrorMessage(e),
            payload: sanitizePayload(ctx.payload),
        });
        throw e;
    }
}

function sanitizePayload(p: any): any {
    if (p == null) return undefined;
    try {
        // Rimuovi campi pesanti/sensibili
        const clone = JSON.parse(JSON.stringify(p));
        for (const k of Object.keys(clone || {})) {
            const v = clone[k];
            if (typeof v === 'string' && v.length > 500) clone[k] = v.slice(0, 500) + '…';
            if (k.toLowerCase().includes('base64') || k.toLowerCase().includes('image')) delete clone[k];
        }
        return clone;
    } catch {
        return undefined;
    }
}

function extractErrorMessage(e: any): string {
    if (!e) return 'unknown';
    if (typeof e === 'string') return e;
    const parts: string[] = [];
    if (e.code) parts.push(`code=${e.code}`);
    if (e.message) parts.push(e.message);
    if (e.response?.data?.message) parts.push(`server=${e.response.data.message}`);
    if (parts.length === 0 && e.toString) return String(e);
    return parts.join(' | ');
}
